import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabel, KeyLabelObject } from 'src/Library/Types';
import {
  Row,
  Form,
  Col,
  Select,
  Input,
  Icon,
  Modal,
  Button,
  InputNumber,
  AutoComplete,
  Radio,
} from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import { uploadSIModalStyles } from './UploadSIModal.styles';
import {
  renderLabelAndInputNumber,
  renderLabelAndTextArea,
  numberLengthValidator,
  renderSelectDropDown,
  renderLabelAndInput,
} from 'src/Library/Common';
import { connect } from 'react-redux';
import { renderWeightOrVolumeUnit, getMandatoryFields } from './UploadSIModal.utils';
import { isValidNumber, isValidArray } from 'src/Library/Helper';
import { ShipmentData } from 'src/components/pages/shipment-execution/SE.Types';
import { CargoTypes } from 'src/Library/Constants';
import { CargoType } from 'src/components/pages/Enquiry/InquiryUtils';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends StylesProps<ReturnType<typeof uploadSIModalStyles>> {
  form;
  isSingleCargo: boolean;
  containerDetails;
  containerKey: number;
  handleRemove: (sealIndex, containerKey) => void;
  handleAddMore: (containerKey) => void;
  volumeUnits: string[];
  weightUnits: string[];
  handleRemoveContainer: (containerKey) => void;
  sealType: KeyLabel[];
  containerTypesAndSizes;
  getPackageTypes: () => void;
  packageTypes;
  isFetchingPackages: boolean;
  shipmentData: ShipmentData;
  containerNoList;
  isFetchingContainers: boolean;
  getContainers: () => void;
  countryId: string;
  SICountryValidations;
  pendingContainers: number;
  cargoToContainerListMap;
  reeferTypes: KeyLabelObject [];
  renderHazProductDetails: (prefix, labelInputProps, initalValuePrefix) => React.ReactElement;
}

interface StateProps {
  isRemoveContainerModalVisible?: boolean;
}

const UploadSIModalContainerAndCargo = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    isSingleCargo,
    containerDetails,
    containerKey,
    handleRemove,
    handleAddMore,
    volumeUnits,
    weightUnits,
    handleRemoveContainer,
    sealType,
    containerTypesAndSizes,
    getPackageTypes,
    packageTypes,
    isFetchingPackages,
    shipmentData,
    containerNoList,
    isFetchingContainers,
    getContainers,
    SICountryValidations,
    countryId,
    pendingContainers,
    cargoToContainerListMap,
    reeferTypes,
    renderHazProductDetails,
  } = props;

  const [state, setState] = useGenericState<StateProps>({

  });

  const {
    isRemoveContainerModalVisible,
  } = state;

  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
  } = form;

  const labelInputProps = {
    colSpan: {
      left: 8,
      right: 8,
    },
    form,
    classes,
    precision: 2,
    min: 0,
  };

  const isHazCargo = shipmentData?.cargoType === CargoTypes.Haz;

  const mandatoryFields = getMandatoryFields({
    SICountryValidations,
    countryId,
    shipmentData,
  });

  const getContainerNumbers = () => {
    const queryString = getFieldValue(`cargoAndContainerDetails.containerDetails[${containerKey}].containerNumber`);
    const containerDetailList = getFieldValue('cargoAndContainerDetails.containerDetails');
    const filledContainerNumbers = isValidArray(containerDetailList) ?
      containerDetailList?.map(item => item.containerNumber) : [];

    if (!queryString) {
      return containerNoList?.filter(
        item => filledContainerNumbers?.indexOf(item?.containerNumber) === -1);
    }

    const filteredContainers = containerNoList?.filter(
      item => filledContainerNumbers?.indexOf(item?.containerNumber) === -1 &&
      item?.containerNumber?.toLowerCase()?.includes(queryString?.toLowerCase()));
    return filteredContainers;
  };

  const handleContainerNumberSelection = (options, containerKey) => {
    setFieldsValue({
      [`cargoAndContainerDetails.containerDetails[${containerKey}].containerSize`]:
        options?.props?.containerSize,
      [`cargoAndContainerDetails.containerDetails[${containerKey}].containerType`]:
        options?.props?.containerType,
    });
  }

  const renderContainerNumber = () : React.ReactElement => {
    const containerNumbers = getContainerNumbers()
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].containerNumber`, {
          rules: [
            { required: true, message: 'Required' },
            { pattern: /^[a-zA-Z]{4}[0-9]{7}$/, message: 'Must contain 4 alphabets followed by 7 numbers' }
          ],
          initialValue: containerDetails && containerDetails[containerKey]?.containerNumber,
        })(
          <AutoComplete
            style={{ width: 200 }}
            placeholder="Container No"
            onFocus={() => getContainers()}
            dropdownRender={menu => renderSelectDropDown(menu, isFetchingContainers)}
            onSelect={(val, options) => handleContainerNumberSelection(options, containerKey)}
          >
            {containerNumbers?.map((container) => (
              <AutoComplete.Option
                key={container?.containerNumber}
                { ...container }
                value={container?.containerNumber}
              >
                {container?.containerNumber}
              </AutoComplete.Option>
            ))}
          </AutoComplete>
        )}
      </Form.Item>
    );
  };

  const renderContainerTypeAndSize = (key, placeholder, arrayList, initialValue?) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(key, {
          rules: [
            { required: true, message: 'Required' },
          ],
          initialValue,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            optionFilterProp="children"
            placeholder={placeholder}
          >
            {arrayList?.map(item =>
              <Select.Option key={item.key}>{item.label}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderSealType = (key, sealIndex) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator(`cargoAndContainerDetails.containerDetails[${key}].sealDetails[${sealIndex}].type`, {
          rules: [{ required: true, message: 'Required' }],
          initialValue: containerDetails && containerDetails[containerKey]?.sealDetails[sealIndex].type,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            allowClear
            optionFilterProp="children"
            placeholder="Select Type"
          >
            {sealType?.map(item =>
              <Select.Option key={item.key}>{item.label}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderSealNumber = () : React.ReactElement => {
    return (
      <React.Fragment>
        {containerDetails[containerKey]?.sealDetails?.map((item, sealIndex) => (
          <Row>
            <Col span={3} className={classes.label}>
              {sealIndex === 0 ? 'Seal Number*' : ''}
            </Col>
            <Col span={7} style={{ paddingRight: '6px' }}>
              {renderSealType(containerKey, sealIndex)}
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: 0 }}>
                {form.getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].sealDetails[${sealIndex}].value`, {
                  rules: [{ required: true, message: 'Required' }],
                  initialValue: containerDetails && containerDetails[containerKey]?.sealDetails[sealIndex].value,
                })(
                  <Input
                    placeholder="Enter Value"
                    className={classes.inputField}
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={1}>
              {(containerDetails[containerKey]?.sealDetails?.length !== 1 &&
                sealIndex !== containerDetails[containerKey]?.sealDetails?.length - 1 &&
                <Icon type="close"
                  style={{ color: 'red', padding: '12px 0 0 6px' }}
                  onClick={() => handleRemove(sealIndex, containerKey)}
                />
              )}
            </Col>
          </Row>
        ))}
        <Row>
          <Col span={3} />
          <div>
            <a
              className={classes.addMore}
              style={{ marginTop: '8px' }}
              onClick={() => handleAddMore(containerKey)}
            >
              + Add More
            </a>
          </div>
        </Row>
      </React.Fragment>
    );
  };

  const handleCloseConfirmation = () : void => {
    setState({ isRemoveContainerModalVisible: false });
  };

  const renderRemoveModalConfirmation = () : React.ReactElement => {
    return (
      <Modal
        title={
          <span><Icon type="exclamation-circle" theme="filled" style={{
            fontSize: '16px',
            color: '#FAB826',
            marginRight: '12px',
          }}/>Confirm Action</span>
        }
        width={400}
        visible={isRemoveContainerModalVisible}
        footer={
          <React.Fragment>
            <span style={{
              cursor: 'pointer',
              fontSize: '12px',
              marginRight: '16px',
            }} onClick={handleCloseConfirmation}>Cancel</span>
            <Button
              onClick={() => {
                handleRemoveContainer(containerKey);
                handleCloseConfirmation();
              }}
              className={classes.addContainerButton}
            >
              Submit
            </Button>
          </React.Fragment>
        }
        onCancel={handleCloseConfirmation}
      >
        <div style={{ fontSize: '16px', marginBottom: '25px' }}>
          This will remove all the
          <strong> {`Container ${containerKey + 1} and Cargo ${containerKey + 1}`} </strong>
          details. Are you sure you want to proceed?
        </div>
      </Modal>
    );
  };

  const renderContainerDetails = () : React.ReactElement => {
    const containerSizeList = containerTypesAndSizes?.find(
      item =>
        item.key === getFieldValue(`cargoAndContainerDetails.containerDetails[${containerKey}].containerType`))?.containerSizes
        || containerTypesAndSizes[0]?.containerSizes;

    const containerTypeList = containerTypesAndSizes?.filter(item => (
      cargoToContainerListMap?.[shipmentData?.cargoType]?.indexOf(item?.key) !== -1
    ));
    const {
      isContainerTareWeightMandatory,
    } = mandatoryFields;
    return (
      <div className={classes.containerDiv}
        style={{ borderBottom: (isSingleCargo && !isHazCargo) ? 'unset' : '1px solid rgba(0,0,0,0.15)' }}>
        <Row>
          <Col span={(isSingleCargo || isHazCargo) ? 15 : 16}>
            <div className={classes.contentHeading}>Container {containerKey + 1}</div>              
            <Row style={{ paddingTop: '8px' }}>
              <Col span={3} className={classes.label}>
                Container*
              </Col>
              <Col span={7} style={{ paddingRight: '6px' }}>
                {renderContainerNumber()}
              </Col>
              <Col span={2} />
              <Col span={12}>
                <Col span={8} className={classes.label}>
                  Container Details*
                </Col>
                <Col span={10}>
                  {renderContainerTypeAndSize(
                    `cargoAndContainerDetails.containerDetails[${containerKey}].containerType`,
                    'Type',
                    containerTypeList,
                    containerDetails && containerDetails[containerKey]?.containerType,
                  )}
                </Col>
                <Col span={6} style={{ paddingLeft: '8px' }}>
                  {renderContainerTypeAndSize(
                    `cargoAndContainerDetails.containerDetails[${containerKey}].containerSize`,
                    'Size',
                    containerSizeList,
                    containerDetails && containerDetails[containerKey]?.containerSize,
                  )}
                </Col>
              </Col>
            </Row>
            {shipmentData?.cargoType === CargoType.REEFER && <Row style={{ margin: '12px 0 -12px 0' }}>
              <Col span={3} className={classes.label}>
                Reefer Type
              </Col>
              <Col span={8}>
                <Form.Item style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.productAdditionalDetails.reeferType`, {
                    initialValue: containerDetails?.[containerKey]?.cargoDetails?.productAdditionalDetails?.reeferType
                      || 'ACTIVE',
                  })(
                    <Radio.Group>
                      {reeferTypes?.map(item  => (
                        <Radio
                          value={item.key}
                          style={{ marginRight: '16px', fontSize: '12px' }}
                          key={item.key}
                          onChange={(e) => {
                            if (e.target.value === 'NON_ACTIVE') {
                              setFieldsValue({
                                [`cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.productAdditionalDetails.temperatureControlled`]: undefined
                              })
                            }
                          }}
                        >
                          {item.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  )}
                </Form.Item>
              </Col>
              <Col span={1} />
              {renderLabelAndInputNumber({
                ...labelInputProps,
                colSpan: {
                  left: 4,
                  right: 3,
                },
                label: 'Temp',
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.productAdditionalDetails.temperatureControlled`,
                initialValue: (containerDetails?.[containerKey]?.cargoDetails?.productAdditionalDetails?.temperatureControlled)
                  ?? undefined,
                placeholder: '0.00',
                rules: [
                  {
                    validator: (rule, value, cb) => {
                      if (value > 998) {
                        cb('Must be less than or equal to 998');
                      } else if (value < -99) {
                        cb('Must be more than or equal to -99');
                      } else cb();
                    },
                  },
                ],
                min: -99,
                max: 998,
                disabled: getFieldValue(
                  `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.productAdditionalDetails.reeferType`
                ) === 'NON_ACTIVE',
              })}
              <Col span={2} style={{ padding: '8px 0 0 8px' }}>°<span style={{ fontWeight: 600 }}>C</span></Col>
            </Row>}
            <Row style={{ paddingTop: '8px' }}>
              {renderLabelAndInputNumber({
                ...labelInputProps,
                colSpan: {
                  left: 3,
                  right: 4,
                },
                label: 'Container Gross Weight',
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].containerGrossWeight.value`,
                initialValue: containerDetails && containerDetails[containerKey]?.containerGrossWeight?.value,
                placeholder: 'Weight',
                precision: 2,
              })}
              <Col span={3} style={{ paddingLeft: '8px' }}>
                {renderWeightOrVolumeUnit(
                  form,
                  classes,
                  `cargoAndContainerDetails.containerDetails[${containerKey}].containerGrossWeight.unit`,
                  weightUnits,
                  containerDetails && containerDetails[containerKey]?.containerGrossWeight?.unit || 'kg',
                )}
              </Col>
              <Col span={2} />
              {renderLabelAndInputNumber({
                ...labelInputProps,
                colSpan: {
                  left: 4,
                  right: 5,
                },
                label: <>Container Tare Weight{isContainerTareWeightMandatory ? <Asterisk /> : null}</>,
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].containerTareWeight.value`,
                initialValue: containerDetails && containerDetails[containerKey]?.containerTareWeight?.value,
                placeholder: 'Weight',
                precision: 3,
                rules: [{ required: isContainerTareWeightMandatory, message: 'Required' }],
              })}
              <Col span={3} style={{ paddingLeft: '8px' }}>
                {renderWeightOrVolumeUnit(
                  form,
                  classes,
                  `cargoAndContainerDetails.containerDetails[${containerKey}].containerTareWeight.unit`,
                  weightUnits,
                  containerDetails && containerDetails[containerKey]?.containerTareWeight?.unit || 'kg',
                )}
              </Col>
            </Row>
            {renderSealNumber()}
          </Col>
          <Col span={(isSingleCargo || isHazCargo) ? 8 : 7} style={{ paddingLeft: '30px' }}>
            {isSingleCargo && !isHazCargo && <div className={classes.singleCargo}>
              <React.Fragment>
                {renderPackageCount()}
                <Row>
                  {renderCargoGrossWeight(
                    { left: 8, right: 6},
                    5,
                    `cargoAndContainerDetails.containerDetails[${containerKey}]`,
                    containerDetails?.[containerKey],
                  )}
                </Row>
              </React.Fragment>
            </div>}
          </Col>
          <Col span={1}>
            {containerDetails?.length > 1 &&
              <Icon
                type="delete"
                style={{ fontSize: '16px' }}
                onClick={() => setState({ isRemoveContainerModalVisible: true })}
              />
            }
          </Col>
        </Row>
      </div>
    )
  };

  const renderPackageCount = () : React.ReactElement => {
    return (
      <div>
        <div className={classes.contentHeading}>Cargo {containerKey + 1}</div>   
        <div className={classes.flexRowDiv} style={{ marginTop: '15px' }}>
          <div className={classes.label} style={{ margin: '8px 12px 0 0' }}>
            Package Count*
          </div>
          <Form.Item style={{ width: '175px', marginBottom: '10px' }}>
            {form.getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].packageCount`, {
              rules: [
                { required: true, message: 'Required' },
              ],
              initialValue: containerDetails && containerDetails[containerKey]?.packageCount,
            })(
              <InputNumber
                placeholder="Enter Count"
                precision={0}
                min={0}
                className={classes.inputNumberField}
              />
            )}
          </Form.Item>
        </div>   
      </div>
    );
  };

  const renderCargoGrossWeight = (weightColSpan, unitColSpan, keyPrefix, initialValuePrefix) : React.ReactElement => {
    return (
      <React.Fragment>
        {renderLabelAndInputNumber({
          ...labelInputProps,
          colSpan: {
            left: weightColSpan?.left,
            right: weightColSpan?.right,
          },
          label: 'Gross Weight*',
          key: `${keyPrefix}.grossWeight.value`,
          initialValue: keyPrefix?.grossWeight?.value || undefined,
          placeholder: '0.00',
          precision: 3,
          rules: [
            { required: true, message: 'Required' },
          ],
        })}
        <Col span={unitColSpan} style={{ paddingLeft: '6px' }}>
          {renderWeightOrVolumeUnit(
            form,
            classes,
            `${keyPrefix}.grossWeight.unit`,
            weightUnits,
            keyPrefix?.grossWeight?.unit || 'kg',
          )}
        </Col>
      </React.Fragment>
    );
  }

  const renderPackageInfo = () : React.ReactElement => {
    const prefillPackageType = containerDetails && containerDetails[containerKey]?.cargoDetails?.packageType;
    return (
      <Row style={{ paddingTop: '12px' }}>
        <Col span={11}>
          <div className={classes.contentLabel}>Package Info*</div>
          <Col span={10} style={{ paddingRight: '8px' }}>
            <Form.Item>
              {form.getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.packageCount`, {
                rules: [
                  { required: true, message: 'Required' },
                ],
                initialValue: containerDetails && containerDetails[containerKey]?.cargoDetails?.packageCount,
              })(
                <InputNumber
                  placeholder="Enter Count"
                  min={0}
                  precision={0}
                  className={classes.inputNumberField}
                />
              )}
            </Form.Item>
          </Col>
          <Col span={13}>
            <Form.Item>
              {getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.packageType`, {
                rules: [
                  { required: true, message: 'Required' },
                ],
                initialValue: prefillPackageType,
              })(
                <Select
                  getPopupContainer={(trigger: any) => trigger.parentNode}
                  className={classes.selectField}
                  showSearch
                  allowClear
                  labelInValue
                  placeholder="Select Type"
                  optionFilterProp="children"
                  onFocus={() => getPackageTypes()}
                  dropdownRender={menu => renderSelectDropDown(menu, isFetchingPackages)}
                >
                  {packageTypes?.map(item =>
                    <Select.Option key={item?.id}>{item?.description}</Select.Option>
                  )}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Col>
        <Col span={1} />
        <Col span={9} style={{ paddingRight: '12px' }}>
          <div className={classes.contentLabel}>Print on B/L as</div>
          <Form.Item>
            {form.getFieldDecorator(`cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.packageTypeOnBl`, {
              initialValue: containerDetails && containerDetails[containerKey]?.cargoDetails?.packageTypeOnBl,
            })(
              <Input
                placeholder="Type"
                className={classes.inputField}
                disabled={!getFieldValue(`cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.packageType`)}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderOtherCargoDetails = () : React.ReactElement => {
    const {
      isHsCodeMandatory,
      isNCMCodeMandatory,
    } = mandatoryFields;
    return (
      <React.Fragment>
        <Row>
          <Col span={10} className={classes.flexColumn}>
            <div>
              {renderLabelAndInputNumber({
                ...labelInputProps,
                colSpan: {
                  left: 6,
                  right: 8,
                },
                label: <>HS Code{isHsCodeMandatory ? <Asterisk /> : null}</>,
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.hsCode`,
                initialValue: containerDetails && containerDetails[containerKey]?.cargoDetails?.hsCode ||
                (pendingContainers === 1 ? shipmentData?.hsCode : undefined),
                placeholder: 'HS Code',
                precision: 0,
                rules: [{ required: isHsCodeMandatory, message: 'Required' }],
              })}
            </div>
            <div>
              {renderLabelAndTextArea({
                ...labelInputProps,
                colSpan: {
                  left: 6,
                  right: 18,
                },
                label: 'Description*',
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.description`,
                initialValue: (containerDetails && containerDetails[containerKey]?.cargoDetails?.description) ||
                  undefined,
                placeholder: 'Description',
                rules: [
                  { required: true, message: 'Required' },
                  { max: 500, message: 'Maximum 500 characters allowed'},
                ],
              })}
            </div>
            <div>
              {renderLabelAndTextArea({
                ...labelInputProps,
                colSpan: {
                  left: 6,
                  right: 18,
                },
                label: 'Marks and Numbers',
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.marksAndNumbers`,
                initialValue: containerDetails && containerDetails[containerKey]?.cargoDetails?.marksAndNumbers,
                placeholder: 'Marks and Numbers',
                rules: [{ max: 35, message: 'Maximum 35 characters allowed' }],
              })}
            </div>
          </Col>
          <Col span={2} />
          <Col span={10} className={classes.flexColumn}>
            <div>
              {renderCargoGrossWeight(
                { left: 6, right: 6},
                6,
                `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails`,
                containerDetails?.[containerKey]?.cargoDetails,
              )}
            </div>
            <div>
              {renderLabelAndInputNumber({
                ...labelInputProps,
                colSpan: {
                  left: 6,
                  right: 6,
                },
                label: 'Gross Volume',
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.grossVolume.value`,
                initialValue: containerDetails && containerDetails[containerKey]?.cargoDetails?.grossVolume?.value,
                placeholder: '0.00',
                precision: 4,
              })}
              <Col span={6} style={{ paddingLeft: '6px' }}>
                {renderWeightOrVolumeUnit(
                  form,
                  classes,
                  `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.grossVolume.unit`,
                  volumeUnits,
                  containerDetails && containerDetails[containerKey]?.cargoDetails?.grossVolume?.unit
                  || 'cbm',
                )}
              </Col>
            </div>
            <div>
              {renderLabelAndInputNumber({
                ...labelInputProps,
                colSpan: {
                  left: 6,
                  right: 8,
                },
                label: <>NCM Code{isNCMCodeMandatory ? <Asterisk /> : null}</>,
                key: `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails.ncmCode`,
                initialValue: containerDetails && containerDetails[containerKey]?.cargoDetails?.ncmCode,
                placeholder: 'NCM Code',
                precision: 0,
                rules: [
                  { required: isNCMCodeMandatory, message: 'Required' },
                  {
                    validator: (rule, value, callback) => numberLengthValidator(rule, value, callback, { max: 140 })
                  },
                ],
              })}
            </div>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const renderCargoDetails = () : React.ReactElement => {
    return (
      <div className={classes.containerDiv} style={{ padding: '16px 0', borderBottom: 'unset' }}>
        {isSingleCargo ? <React.Fragment>
          {renderPackageCount()}
          <Row>
            {renderCargoGrossWeight(
              { left: 8, right: 6},
              5,
              `cargoAndContainerDetails.containerDetails[${containerKey}]`,
              containerDetails?.[containerKey],
            )}
          </Row>
        </React.Fragment> : <React.Fragment>
          <div className={classes.contentHeading}>
            Cargo {containerKey + 1}
          </div>
          {renderPackageInfo()}
          {renderOtherCargoDetails()}
        </React.Fragment>}
        {(!isSingleCargo && isHazCargo) &&
        renderHazProductDetails(
          `cargoAndContainerDetails.containerDetails[${containerKey}].cargoDetails`,
          {
            colSpan: {
              left: 3,
              right: 6,
            },
            form,
            classes,
            precision: 1,
            min: -999,
            max: 999,
          },
          containerDetails?.[containerKey]?.cargoDetails,
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      {renderContainerDetails()}
      {(!isSingleCargo || isHazCargo) && renderCargoDetails()}
      {isRemoveContainerModalVisible && renderRemoveModalConfirmation()}
    </React.Fragment>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  weightUnits: masterData.execution?.validWeightUnits,
  volumeUnits: masterData.execution?.validVolumeUnits,
  sealType: masterData.shippingInstructionsMasterData?.sealType,
  containerTypesAndSizes: masterData.shippingInstructionsMasterData?.containerTypesAndSizes,
  SICountryValidations: masterData.shippingInstructionsMasterData?.SICountryValidations,
  cargoToContainerListMap: masterData.execution?.cargoToContainerListMap,
  reeferTypes: masterData.shippingInstructionsMasterData?.reeferTypes,
});

export default connect(
  mapStateToProps,
  null,
  )(React.memo(withStyles(uploadSIModalStyles)(UploadSIModalContainerAndCargo)));